import type {
  Database,
  Json,
  Tables,
  TablesInsert,
  TablesUpdate,
} from "@punchup/data/supabase.types";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import {
  siDiscord,
  siFacebook,
  siInstagram,
  siReddit,
  siTiktok,
  siX,
  siYoutube,
} from "simple-icons";

import { Constants } from "@punchup/data/supabase.types";
import dayjs from "@punchup/utils/dayjs";
import { Globe } from "lucide-react";
import { SupabaseClient } from "@supabase/supabase-js";
import { VenueInfoTag } from "../client/components/VenueInfoTags";
import { getApiClient } from "@punchup/utils/apiRegistry";
import type { VenueRow } from "@punchup/data/legacy/venues";

import {
  VenuePagesRow,
  VenuePagesUpdate,
  VenuePageTimezoneEntry,
  VenuePageCtaBanner,
} from "@punchup/data/models/venue_pages";
export type { VenuePagesRow, VenuePagesUpdate, VenuePageCtaBanner };

/**
 * Resolve the timezone for a specific show at a venue_page.
 *
 * A venue_page can host multiple locations, each with its own timezone
 * (`venue_pages.timezones: Array<{ location_id, timezone }>`, where
 * `location_id` matches `venues.id` — the same id space referenced by
 * `comedian_shows.venue_id`). Pass the show's `venue_id` to get the tz for
 * *that* location; falls back to the first entry when the show's location
 * isn't in the array (or `venueId` is unknown), then to `"UTC"`.
 */
export function resolveVenueTimezone(
  timezones: VenuePageTimezoneEntry[] | null | undefined,
  venueId: string | null | undefined
): string {
  const entries = timezones ?? [];
  if (venueId) {
    const match = entries.find((entry) => entry.location_id === venueId);
    if (match) return match.timezone;
  }
  return entries[0]?.timezone ?? "UTC";
}

export type VenueWithLocations = VenuePagesRow & {
  locations: Partial<VenueRow>[];
};

export type VenuePagesAdmin = Tables<"venue_pages_admins">;
export type VenuePageCustomLink = Tables<"venue_pages_custom_links">;
export type VenuePageExternalLink = Tables<"venue_pages_external_links">;
export type VenuePageFAQ = Tables<"venue_pages_faqs">;
export type VenuePageNavItemRow = Tables<"venue_pages_nav_items">;
export type VenuePageTheme = Tables<"venue_pages_themes">;

/**
 * Whether a venue page serves its public routes to anyone.
 *
 * An unpublished page 404s for the public — `apps/pages` and `getVenuePageData`
 * both fall through to an admin-authorization check and 404 when it fails — so
 * nothing may hand out a link into a page that fails this.
 */
export function isVenuePagePublished(
  venuePage: Pick<VenuePagesRow, "is_live" | "published_at">
): boolean {
  return !!venuePage.is_live && dayjs(venuePage.published_at).isBefore(dayjs());
}

export async function getVenuePage(
  supabase: SupabaseClient,
  identifier: { id?: string; slug?: string }
): Promise<VenuePagesRow> {
  const { error, data } = await supabase
    .from("venue_pages")
    .select("*")
    .eq(identifier.id ? "id" : "slug", identifier.id ?? identifier.slug)
    .maybeSingle()
    .throwOnError();

  if (error) {
    logException(error, {
      extra: {
        data: "getVenuePage",
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.VenuePages,
      },
    });
    throw error;
  }

  if (!data) {
    const idType = identifier.id ? "id" : "slug";
    const value = identifier.id ?? identifier.slug;
    const error = new Error(`No venue found with ${idType}: ${value}`);

    logException(error, {
      extra: {
        data: "getVenuePage",
      },
      tags: {
        feature: SentryFeatureTags.VenuePages,
      },
    });
    throw error;
  }

  return data;
}

/**
 * Fetch many venue pages in one query. A missing id is absent from the result, not
 * a throw. Order is not guaranteed.
 */
export async function getVenuePagesByIDs(
  supabase: SupabaseClient<Database>,
  venuePageIds: string[]
): Promise<VenuePagesRow[]> {
  const uniqueIds = Array.from(new Set(venuePageIds));
  if (uniqueIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("venue_pages")
    .select("*")
    .in("id", uniqueIds);

  if (error) {
    logException(error, {
      extra: { data: "getVenuePagesByIDs" },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.VenuePages,
      },
    });
    throw error;
  }

  return data ?? [];
}

type UpdateVenuePublishedAtParams = {
  type: "published_at";
  venuePageId: string;
  publishedAt: string;
};

type UpdateVenueLiveStatusParams = {
  type: "live";
  venuePageId: string;
  isLive: boolean;
};

type UpdateVenueTagsParams = {
  type: "tags";
  venuePageId: string;
  newTags: NonNullable<VenuePagesUpdate["info_tags"]>;
};

type UpdateVenueDescriptionParams = {
  type: "description";
  venuePageId: string;
  newDescription: NonNullable<VenuePagesUpdate["description"]>;
};
type UpdateVenueExternalSiteInfoParams = {
  type: "externalSiteInfo";
  venuePageId: string;
  newExternalSiteInfo: VenueExternalSiteInfo[];
};

type UpdateVenueHoursParams = {
  type: "hours";
  venuePageId: string;
  newHours: Record<string, string>;
};

type UpdateVenueLogoImageParams = {
  type: "logoImage";
  venuePageId: string;
  newLogoImageId: string;
};

type UpdateVenueNameParams = {
  type: "name";
  venuePageId: string;
  newName: string;
};

type UpdateVenueSlugParams = {
  type: "slug";
  venuePageId: string;
  newSlug: string;
};

type UpdateVenueCityStateParams = {
  type: "city_state";
  venuePageId: string;
  newCityState: string;
};

type UpdateVenueIsLocationHiddenParams = {
  type: "is_location_hidden";
  venuePageId: string;
  newIsLocationHidden: boolean;
};

type UpdateVenueLocationIdsParams = {
  type: "location_ids";
  venuePageId: string;
  newLocationIds: string[];
};

type UpdateVenuePhoneParams = {
  type: "phone";
  venuePageId: string;
  newPhone: string;
};

type UpdateVenueEmailParams = {
  type: "email";
  venuePageId: string;
  newEmail: string;
};

type UpdateVenueFoodMenuParams = {
  type: "food_menu";
  venuePageId: string;
  newFoodMenu: string;
};

type UpdateVenueDrinksMenuParams = {
  type: "drinks_menu";
  venuePageId: string;
  newDrinksMenu: string;
};

type UpdateVenueDirectionsInstructionsParams = {
  type: "directions_instructions";
  venuePageId: string;
  newDirectionsInstructions: string;
};

type UpdateVenueParkingInstructionsParams = {
  type: "parking_instructions";
  venuePageId: string;
  newParkingInstructions: string;
};

type UpdateVenueDefaultShowViewParams = {
  type: "default_show_view";
  venuePageId: string;
  newDefaultShowView: VenuePagesRow["default_show_view"];
};

type UpdateVenueShowsGroupingModeParams = {
  type: "shows_grouping_mode";
  venuePageId: string;
  newShowsGroupingMode: VenuePagesRow["shows_grouping_mode"];
};

type UpdateVenueCtaBannerParams = {
  type: "cta_banner";
  venuePageId: string;
  newCtaBanner: VenuePageCtaBanner | null;
};

type UpdateVenuePageParams =
  | UpdateVenueTagsParams
  | UpdateVenueDescriptionParams
  | UpdateVenueExternalSiteInfoParams
  | UpdateVenueHoursParams
  | UpdateVenueLogoImageParams
  | UpdateVenueLiveStatusParams
  | UpdateVenuePublishedAtParams
  | UpdateVenueNameParams
  | UpdateVenueSlugParams
  | UpdateVenueCityStateParams
  | UpdateVenueIsLocationHiddenParams
  | UpdateVenueLocationIdsParams
  | UpdateVenuePhoneParams
  | UpdateVenueEmailParams
  | UpdateVenueFoodMenuParams
  | UpdateVenueDrinksMenuParams
  | UpdateVenueDirectionsInstructionsParams
  | UpdateVenueParkingInstructionsParams
  | UpdateVenueDefaultShowViewParams
  | UpdateVenueShowsGroupingModeParams
  | UpdateVenueCtaBannerParams;

const generateUpdateData = (
  params: UpdateVenuePageParams
): VenuePagesUpdate => {
  switch (params.type) {
    case "tags":
      return { info_tags: params.newTags };
    case "description":
      return { description: params.newDescription };
    case "externalSiteInfo":
      return {
        external_site_info: params.newExternalSiteInfo,
      };
    case "logoImage":
      return { logo_img: params.newLogoImageId };
    case "hours":
      return { hours: params.newHours };
    case "live":
      return { is_live: params.isLive };
    case "published_at":
      return { published_at: params.publishedAt };
    case "name":
      return { name: params.newName };
    case "slug":
      return { slug: params.newSlug };
    case "city_state":
      return { city_state: params.newCityState };
    case "is_location_hidden":
      return { is_location_hidden: params.newIsLocationHidden };
    case "location_ids":
      return { location_ids: params.newLocationIds };
    case "phone":
      return { phone: params.newPhone };
    case "email":
      return { email: params.newEmail };
    case "food_menu":
      return { food_menu: params.newFoodMenu };
    case "drinks_menu":
      return { drinks_menu: params.newDrinksMenu };
    case "directions_instructions":
      return { directions_instructions: params.newDirectionsInstructions };
    case "parking_instructions":
      return { parking_instructions: params.newParkingInstructions };
    case "default_show_view":
      return { default_show_view: params.newDefaultShowView };
    case "shows_grouping_mode":
      return { shows_grouping_mode: params.newShowsGroupingMode };
    case "cta_banner":
      return { cta_banner: params.newCtaBanner as unknown as Json };
    default: {
      const error = new Error(`Unknown update type: ${params}`);
      logException(error, {
        extra: {
          data: "generateUpdateData",
        },
        tags: {
          feature: SentryFeatureTags.VenuePages,
        },
      });
      throw error;
    }
  }
};

// Do not export this function, create a helper function as done below
async function updateVenuePage(
  supabase: SupabaseClient,
  params: UpdateVenuePageParams
): Promise<boolean> {
  const { venuePageId, type } = params;

  const updateData = generateUpdateData(params);

  const { data } = await supabase
    .from("venue_pages")
    .update(updateData)
    .eq("id", venuePageId)
    .select()
    .single()
    .throwOnError();

  if (!data) {
    throw new Error("No data returned from database");
  }

  switch (type) {
    case "tags": {
      if (!Array.isArray(data.info_tags)) {
        throw new Error(
          `info_tags is not an array. Received: ${typeof data.info_tags}`
        );
      }
      const updatedTags = data.info_tags as VenueInfoTag[];
      const tagsMatch =
        JSON.stringify(updatedTags) === JSON.stringify(params.newTags);
      if (!tagsMatch) {
        throw new Error(
          "Failed to validate venue info tags update. Received: " +
            JSON.stringify(updatedTags) +
            " Expected: " +
            JSON.stringify(params.newTags)
        );
      }
      break;
    }
    case "description": {
      if (typeof data.description !== "string") {
        throw new Error(
          `description is not a string. Received: ${typeof data.description}`
        );
      }
      if (data.description !== params.newDescription) {
        throw new Error(
          "Failed to validate venue description update. Received: " +
            data.description +
            " Expected: " +
            params.newDescription
        );
      }
      break;
    }
    case "externalSiteInfo": {
      if (!Array.isArray(data.external_site_info)) {
        throw new Error(
          `external_site_info is not an array. Received: ${typeof data.external_site_info}`
        );
      }
      const updatedArray = data.external_site_info as VenueExternalSiteInfo[];
      const updatedSocialInfo = updatedArray.find(
        (item) => item.type === "social"
      );
      if (!updatedSocialInfo) {
        throw new Error("Failed to find social info in external_site_info");
      }
      // Normalize the social links by sorting their keys before comparing.
      const normalize = (obj: SocialLinks) =>
        JSON.stringify(
          Object.keys(obj)
            .sort()
            .reduce((acc, key) => {
              acc[key as keyof SocialLinks] = obj[key as keyof SocialLinks];
              return acc;
            }, {} as SocialLinks)
        );

      const normalizedUpdatedLinks = normalize(updatedSocialInfo.links);
      const normalizedExpectedLinks = normalize(
        params.newExternalSiteInfo[0].links
      );

      if (normalizedUpdatedLinks !== normalizedExpectedLinks) {
        console.error("Updated social info from DB:", updatedSocialInfo.links);
        console.error(
          "Expected social links:",
          params.newExternalSiteInfo[0].links
        );
        throw new Error("Failed to validate external site info update");
      }
      break;
    }
    case "hours": {
      if (typeof data.hours !== "object") {
        throw new Error(
          `hours is not an object. Received: ${typeof data.hours}`
        );
      }
      // Validate that the hours received after the update are the same as the hours originally submitted
      // Compare the two objects after sorting them to make sure they are indeed the same
      if (
        JSON.stringify(
          Object.fromEntries(Object.entries(data.hours).sort())
        ) !==
        JSON.stringify(
          Object.fromEntries(Object.entries(params.newHours).sort())
        )
      ) {
        throw new Error(
          "Failed to validate venue hours update. Received: " +
            JSON.stringify(data.hours) +
            " Expected: " +
            JSON.stringify(params.newHours)
        );
      }

      break;
    }
    case "logoImage": {
      if (typeof data.logo_img !== "string") {
        throw new Error(
          `logo_img is not a string. Received: ${typeof data.logo_img}`
        );
      }
      if (data.logo_img !== params.newLogoImageId) {
        throw new Error(
          "Failed to validate venue logo image update. Received: " +
            data.logo_img +
            " Expected: " +
            params.newLogoImageId
        );
      }
      break;
    }
    case "live": {
      if (typeof params.isLive !== "boolean") {
        throw new Error(
          `isLive is not a boolean. Received: ${typeof params.isLive}`
        );
      }
      break;
    }
    case "published_at": {
      if (typeof data.published_at !== "string") {
        throw new Error(
          `published_at is not a string. Received: ${typeof data.published_at}`
        );
      }
      break;
    }
    case "name": {
      if (typeof data.name !== "string" || data.name !== params.newName) {
        throw new Error("Failed to validate venue name update");
      }
      break;
    }
    case "slug": {
      if (typeof data.slug !== "string" || data.slug !== params.newSlug) {
        throw new Error("Failed to validate venue slug update");
      }
      break;
    }
    case "city_state": {
      if (
        typeof data.city_state !== "string" ||
        data.city_state !== params.newCityState
      ) {
        throw new Error("Failed to validate venue city_state update");
      }
      break;
    }
    case "is_location_hidden": {
      if (
        typeof data.is_location_hidden !== "boolean" ||
        data.is_location_hidden !== params.newIsLocationHidden
      ) {
        throw new Error("Failed to validate venue is_location_hidden update");
      }
      break;
    }
    case "location_ids": {
      if (
        !Array.isArray(data.location_ids) ||
        JSON.stringify(data.location_ids.sort()) !==
          JSON.stringify(params.newLocationIds.sort())
      ) {
        throw new Error("Failed to validate venue location_ids update");
      }
      break;
    }
    case "default_show_view": {
      if (data.default_show_view !== params.newDefaultShowView) {
        throw new Error("Failed to validate venue default_show_view update");
      }
      break;
    }
    case "shows_grouping_mode": {
      if (data.shows_grouping_mode !== params.newShowsGroupingMode) {
        throw new Error("Failed to validate venue shows_grouping_mode update");
      }
      break;
    }
  }

  return true;
}

export async function updateVenueFoodMenu({
  supabase,
  venuePageId,
  newFoodMenu,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newFoodMenu: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "food_menu",
    venuePageId,
    newFoodMenu,
  });
}

export async function updateVenueDrinksMenu({
  supabase,
  venuePageId,
  newDrinksMenu,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newDrinksMenu: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "drinks_menu",
    venuePageId,
    newDrinksMenu,
  });
}

export async function updateVenueDirectionsInstructions({
  supabase,
  venuePageId,
  newDirectionsInstructions,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newDirectionsInstructions: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "directions_instructions",
    venuePageId,
    newDirectionsInstructions,
  });
}

export async function updateVenueParkingInstructions({
  supabase,
  venuePageId,
  newParkingInstructions,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newParkingInstructions: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "parking_instructions",
    venuePageId,
    newParkingInstructions,
  });
}

export async function updateVenuePhone({
  supabase,
  venuePageId,
  newPhone,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newPhone: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "phone",
    venuePageId,
    newPhone,
  });
}

export async function updateVenueEmail({
  supabase,
  venuePageId,
  newEmail,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newEmail: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "email",
    venuePageId,
    newEmail,
  });
}

export async function updateVenueLiveStatus({
  supabase,
  venuePageId,
  isLive,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  isLive: boolean;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "live",
    venuePageId,
    isLive,
  });
}
export async function updateVenuePublishedAt({
  supabase,
  venuePageId,
  publishedAt,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  publishedAt: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "published_at",
    venuePageId,
    publishedAt,
  });
}

export async function updateVenueLogoImage({
  supabase,
  venuePageId,
  newLogoImageId,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newLogoImageId: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "logoImage",
    venuePageId,
    newLogoImageId,
  });
}

export async function updateVenueInfoTags({
  supabase,
  venuePageId,
  newTags,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newTags: VenueInfoTag[];
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "tags",
    venuePageId,
    newTags: newTags as unknown as Json[],
  });
}

export async function updateVenueDescription({
  supabase,
  venuePageId,
  newDescription,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newDescription: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "description",
    venuePageId,
    newDescription,
  });
}

export async function updateVenueHours({
  supabase,
  venuePageId,
  newHours,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newHours: Record<string, string>;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "hours",
    venuePageId,
    newHours,
  });
}

export async function updateVenueName({
  supabase,
  venuePageId,
  newName,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newName: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "name",
    venuePageId,
    newName,
  });
}

export async function updateVenueSlug({
  supabase,
  venuePageId,
  newSlug,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newSlug: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "slug",
    venuePageId,
    newSlug,
  });
}

export async function updateVenueCityState({
  supabase,
  venuePageId,
  newCityState,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newCityState: string;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "city_state",
    venuePageId,
    newCityState,
  });
}

export async function updateVenueIsLocationHidden({
  supabase,
  venuePageId,
  newIsLocationHidden,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newIsLocationHidden: boolean;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "is_location_hidden",
    venuePageId,
    newIsLocationHidden,
  });
}

export async function updateVenueLocationIds({
  supabase,
  venuePageId,
  newLocationIds,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newLocationIds: string[];
}): Promise<boolean> {
  const ok = await updateVenuePage(supabase, {
    type: "location_ids",
    venuePageId,
    newLocationIds,
  });
  await triggerVenuePageTimezoneRecompute(venuePageId);
  return ok;
}

/**
 * Fire a recompute of `venue_pages.timezones` after a location_ids mutation.
 * The server route holds the Google Time Zone API key; this wrapper is safe to
 * call from the browser.
 *
 * Logs but never throws — a failed recompute must not roll back the primary
 * mutation the caller just made.
 */
export async function triggerVenuePageTimezoneRecompute(
  venuePageId: string
): Promise<void> {
  try {
    await getApiClient().post(
      `/api/v2/manager/venue-pages/${venuePageId}/recompute-timezones`,
      undefined
    );
  } catch (error) {
    logException(error, {
      extra: { data: "triggerVenuePageTimezoneRecompute", venuePageId },
      tags: { feature: SentryFeatureTags.VenuePages },
    });
  }
}

export async function updateVenueDefaultShowView({
  supabase,
  venuePageId,
  newDefaultShowView,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newDefaultShowView: VenuePagesRow["default_show_view"];
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "default_show_view",
    venuePageId,
    newDefaultShowView,
  });
}

export async function updateVenueShowsGroupingMode({
  supabase,
  venuePageId,
  newShowsGroupingMode,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newShowsGroupingMode: VenuePagesRow["shows_grouping_mode"];
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "shows_grouping_mode",
    venuePageId,
    newShowsGroupingMode,
  });
}

export async function updateVenueCtaBanner({
  supabase,
  venuePageId,
  newCtaBanner,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  newCtaBanner: VenuePageCtaBanner | null;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "cta_banner",
    venuePageId,
    newCtaBanner,
  });
}

// Define the SocialLinks interface (same as in SocialSettingsForm)

export enum SocialType {
  Instagram = "instagram",
  Facebook = "facebook",
  Youtube = "youtube",
  Tiktok = "tiktok",
  Twitter = "twitter",
  Reddit = "reddit",
  Discord = "discord",
  Website = "website",
}

export type SocialLinks = Record<SocialType, string>;

interface SocialTypeConfig {
  icon: typeof siX | typeof Globe;
  placeholder: string;
}

export const SOCIAL_TYPE_CONFIG: Record<SocialType, SocialTypeConfig> = {
  [SocialType.Twitter]: {
    icon: siX,
    placeholder: "X URL (e.g., https://x.com/username)",
  },
  [SocialType.Facebook]: {
    icon: siFacebook,
    placeholder: "Facebook URL (e.g., https://facebook.com/username)",
  },
  [SocialType.Instagram]: {
    icon: siInstagram,
    placeholder: "Instagram URL (e.g., https://instagram.com/username)",
  },
  [SocialType.Youtube]: {
    icon: siYoutube,
    placeholder: "Youtube URL (e.g., https://youtube.com/username)",
  },
  [SocialType.Website]: {
    icon: Globe,
    placeholder: "Website URL (e.g., https://example.com)",
  },
  [SocialType.Tiktok]: {
    icon: siTiktok,
    placeholder: "TikTok URL (e.g., https://tiktok.com/@username)",
  },
  [SocialType.Reddit]: {
    icon: siReddit,
    placeholder: "Reddit URL (e.g., https://reddit.com/username)",
  },
  [SocialType.Discord]: {
    icon: siDiscord,
    placeholder: "Discord URL (e.g., https://discord.com/username)",
  },
};

// Define an interface for the external site info structure
export interface VenueExternalSiteInfo {
  type: "social";
  links: SocialLinks;
  [key: string]: Json | undefined;
}

/**
 * Updates the external_site_info column for a given venue page.
 * The external_site_info column is expected to be a jsonb[] (an array of JSON objects).
 * This function retrieves the current array, updates or inserts the "social" object,
 * and writes the updated array back to the database.
 */
export async function updateVenueExternalSiteInfo({
  supabase,
  venuePageId,
  socialLinks,
}: {
  supabase: SupabaseClient;
  venuePageId: string;
  socialLinks: SocialLinks;
}): Promise<boolean> {
  return updateVenuePage(supabase, {
    type: "externalSiteInfo",
    venuePageId,
    newExternalSiteInfo: [{ type: "social", links: socialLinks }],
  });
}

export async function getVenueSocialLinks(
  supabase: SupabaseClient,
  venueId: string
): Promise<{ links: Record<string, string> }> {
  const { data } = await supabase
    .from("venue_pages")
    .select("external_site_info")
    .eq("id", venueId)
    .maybeSingle()
    .throwOnError();

  if (!data?.external_site_info || !Array.isArray(data.external_site_info)) {
    return { links: {} };
  }

  const socialInfo = data.external_site_info.find(
    (item) => item.type === "social"
  ) as { type: string; links: Record<string, string> } | undefined;

  return { links: socialInfo?.links ?? {} };
}

/**
 * The one row the crawl-apply update path needs: the page's linked venues, curated hours, and
 * existing social links — in a single read. Returns null when the page is gone, so the caller can
 * 404 (and distinguish that from a thrown DB error → 500). Replaces an inline select + a separate
 * getVenueSocialLinks round-trip on the same row.
 */
export async function getVenuePageApplyTarget(
  supabase: SupabaseClient,
  venuePageId: string
): Promise<{
  locationIds: string[];
  hours: Record<string, string>;
  socialLinks: Record<string, string>;
} | null> {
  const { data, error } = await supabase
    .from("venue_pages")
    .select("location_ids, hours, external_site_info")
    .eq("id", venuePageId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const hours =
    data.hours && typeof data.hours === "object" && !Array.isArray(data.hours)
      ? (data.hours as Record<string, string>)
      : {};

  const socialInfo =
    data.external_site_info && Array.isArray(data.external_site_info)
      ? (data.external_site_info.find((item) => item.type === "social") as
          | { type: string; links: Record<string, string> }
          | undefined)
      : undefined;

  return {
    locationIds: data.location_ids ?? [],
    hours,
    socialLinks: socialInfo?.links ?? {},
  };
}

// Add this function alongside other venue update functions
export const updateVenueVideo = async ({
  supabase,
  venuePageId,
  videoId,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  videoId: string;
}) => {
  const { error } = await supabase
    .from("venue_pages")
    .update({ video_id: videoId })
    .eq("id", venuePageId);

  if (error) throw error;
};

export async function getAllVenuePages(supabase: SupabaseClient) {
  const { data, error } = await supabase
    .from("venue_pages")
    .select("*")
    .order("name", { ascending: true });

  if (error) {
    throw error;
  }

  return data;
}

// LIKE-escape so a user-typed %, _ or \ matches literally rather than acting as a wildcard.
function escapeLikePattern(value: string): string {
  return value.replace(/[\\%_]/g, (ch) => `\\${ch}`);
}

/**
 * The venue-page picker list for crawl attribution: id/name/slug/city_state/is_producer/location_ids,
 * name-ordered, capped at 50, and filtered by a case-insensitive name substring when `q` is non-empty.
 * Filter + projection + cap run in ONE bounded SQL query (not a full-table read filtered in memory),
 * so it can't trip the PostgREST row cap as the table grows.
 */
export async function searchVenuePagesForPicker(
  supabase: SupabaseClient,
  q: string
) {
  let filter = supabase
    .from("venue_pages")
    .select("id, name, slug, city_state, is_producer, location_ids");
  const trimmed = q.trim();
  if (trimmed) filter = filter.ilike("name", `%${escapeLikePattern(trimmed)}%`);
  const { data, error } = await filter
    .order("name", { ascending: true })
    .limit(50);
  if (error) throw error;
  return data ?? [];
}

/**
 * The venues linked to a page, for the crawl-attribution "which venue" dropdown. A superAdmin tool,
 * so — unlike the public getVenueWithLocations — it does NOT hide on `is_location_hidden`, and it
 * returns a lean id/name/address/city/state projection. null when the page is gone (caller 404s),
 * [] when it has no linked venues.
 */
export async function getLinkedVenuesForPicker(
  supabase: SupabaseClient,
  venuePageId: string
): Promise<
  | {
      id: string;
      name: string | null;
      address: string | null;
      city: string | null;
      state: string | null;
    }[]
  | null
> {
  const { data: page } = await supabase
    .from("venue_pages")
    .select("location_ids")
    .eq("id", venuePageId)
    .maybeSingle();
  if (!page) return null;

  const ids = page.location_ids ?? [];
  if (ids.length === 0) return [];

  const { data, error } = await supabase
    .from("venues")
    .select("id, name, address, city, state")
    .in("id", ids);
  if (error) throw error;
  return data ?? [];
}

export async function getVenuePageBySlug(
  supabase: SupabaseClient<Database>,
  slug: string
) {
  const { data, error } = await supabase
    .from("venue_pages")
    .select("*")
    .eq("slug", slug)
    .limit(1)
    .maybeSingle();

  if (error) {
    logException(error, {
      extra: {
        data: "checkIfVenuePageSlugExists",
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.VenuePages,
      },
    });
    throw error;
  }

  return data;
}

export async function createVenuePage(
  supabase: SupabaseClient,
  venuePage: TablesInsert<"venue_pages">
) {
  const { data, error } = await supabase
    .from("venue_pages")
    .insert(venuePage)
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: {
        data: "createVenuePage",
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.VenuePages,
      },
    });
    throw error;
  }

  await triggerVenuePageTimezoneRecompute(data.id);

  return data;
}

export async function updateVenuePageById(
  supabase: SupabaseClient,
  id: Tables<"venue_pages">["id"],
  venuePage: TablesUpdate<"venue_pages">
) {
  const { data, error } = await supabase
    .from("venue_pages")
    .update(venuePage)
    .eq("id", id)
    .select()
    .single()
    .throwOnError();

  if (error) {
    logException(error, {
      extra: {
        data: "createVenuePage",
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.VenuePages,
      },
    });
    throw error;
  }

  if (venuePage.location_ids !== undefined) {
    await triggerVenuePageTimezoneRecompute(id);
  }

  return data;
}

/*
 * this function will insert a venue page admin row if one does not exist
 * or ignore the existing row if it does exist
 */
export async function upsertVenuePageAdmins(
  supabase: SupabaseClient<Database>,
  venuePageAdmins: TablesInsert<"venue_pages_admins">[]
) {
  const { data, error } = await supabase
    .from("venue_pages_admins")
    .upsert(venuePageAdmins, {
      onConflict: "venue_page_id,admin_id",
      ignoreDuplicates: true, // ignore the existing row if it does exist
    })
    .select();

  if (error) {
    logException(error, {
      extra: {
        data: "upsertVenuePageAdmins",
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.VenuePages,
      },
    });
    throw error;
  }

  return data;
}

export async function deleteVenuePageAdmins(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  adminIds: string[]
) {
  const { data, error } = await supabase
    .from("venue_pages_admins")
    .delete()
    .in("admin_id", adminIds)
    .eq("venue_page_id", venuePageId);

  if (error) {
    logException(error, {
      extra: {
        data: "deleteVenuePageAdmins",
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.VenuePages,
      },
    });
    throw error;
  }

  return data;
}

export async function getVenuePageAdminEmails(
  supabase: SupabaseClient,
  venuePageId: string
) {
  const { data, error } = await supabase
    .from("venue_pages_admins")
    .select("admin_id")
    .eq("venue_page_id", venuePageId);

  if (error) {
    logException(error, {
      extra: { data: "getVenuePageAdminEmails:venue_pages_admins" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  if (!data || data.length === 0) return [];

  const adminIds = data.map((row) => row.admin_id);

  // RLS policy ensures that only super admins can retrieve emails from "users_extra" table
  const { data: users, error: usersError } = await supabase
    .from("users_extra")
    .select("email")
    .in("id", adminIds);

  if (usersError) {
    logException(usersError, {
      extra: { data: "getVenuePageAdminEmails:users_extra" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw usersError;
  }

  return users.map((user) => user.email);
}

export async function getVenuePageCustomLinks(
  supabase: SupabaseClient<Database>,
  venuePageId: string
) {
  const { data, error } = await supabase
    .from("venue_pages_custom_links")
    .select("*")
    .eq("venue_page_id", venuePageId);

  if (error) {
    logException(error, {
      extra: { data: "getVenuePageCustomLinks" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function getVenuePageExternalLinks(
  supabase: SupabaseClient<Database>,
  venuePageId: string
) {
  const { data, error } = await supabase
    .from("venue_pages_external_links")
    .select("*")
    .eq("venue_page_id", venuePageId);

  if (error) {
    logException(error, {
      extra: { data: "getVenuePageExternalLinks" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function createVenuePageCustomLink({
  supabase,
  venuePageId,
  title,
  label,
  path,
  body,
  tixologiTagId,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  title: string;
  label: string;
  path: string;
  body: string;
  tixologiTagId?: number | null;
}) {
  const { data, error } = await supabase
    .from("venue_pages_custom_links")
    .insert({
      venue_page_id: venuePageId,
      title,
      label,
      path,
      body,
      tixologi_tag_id: tixologiTagId ?? null,
    })
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "createVenuePageCustomLink" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function updateVenuePageCustomLink({
  supabase,
  linkId,
  title,
  label,
  path,
  body,
  tixologiTagId,
}: {
  supabase: SupabaseClient<Database>;
  linkId: number;
  title: string;
  label: string;
  path: string;
  body: string;
  tixologiTagId?: number | null;
}) {
  const { data, error } = await supabase
    .from("venue_pages_custom_links")
    .update({
      title,
      label,
      path,
      body,
      // tixologiTagId is the source of truth when provided; pass NULL through
      // explicitly so admins can clear a tag-filtered page back to static.
      ...(tixologiTagId !== undefined
        ? { tixologi_tag_id: tixologiTagId }
        : {}),
    })
    .eq("id", linkId)
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "updateVenuePageCustomLink" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function upsertVenuePageBuiltInLink({
  supabase,
  venuePageId,
  path,
  label,
  title,
  body,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  path: string;
  label: string;
  title: string;
  body: string;
}) {
  // Find-or-create rather than `.upsert(..., onConflict)` so the path doesn't
  // depend on the (venue_page_id, path) unique constraint being present —
  // the constraint is a defensive invariant, not a runtime requirement.
  const { data: existing, error: selectError } = await supabase
    .from("venue_pages_custom_links")
    .select("id")
    .eq("venue_page_id", venuePageId)
    .eq("path", path)
    .maybeSingle();

  if (selectError) {
    logException(selectError, {
      extra: { data: "upsertVenuePageBuiltInLink:select" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw selectError;
  }

  if (existing) {
    const { data, error } = await supabase
      .from("venue_pages_custom_links")
      .update({ label, title, body })
      .eq("id", existing.id)
      .select()
      .single();

    if (error) {
      logException(error, {
        extra: { data: "upsertVenuePageBuiltInLink:update" },
        tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
      });
      throw error;
    }

    return data;
  }

  const { data, error } = await supabase
    .from("venue_pages_custom_links")
    .insert({
      venue_page_id: venuePageId,
      path,
      label,
      title,
      body,
    })
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "upsertVenuePageBuiltInLink:insert" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function deleteVenuePageCustomLink({
  supabase,
  linkId,
}: {
  supabase: SupabaseClient<Database>;
  linkId: number;
}) {
  const { error } = await supabase
    .from("venue_pages_custom_links")
    .delete()
    .eq("id", linkId);

  if (error) {
    logException(error, {
      extra: { data: "deleteVenuePageCustomLink" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return true;
}

export async function createVenuePageExternalLink({
  supabase,
  venuePageId,
  label,
  url,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  label: string;
  url: string;
}) {
  const { data, error } = await supabase
    .from("venue_pages_external_links")
    .insert({
      venue_page_id: venuePageId,
      label,
      url,
    })
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "createVenuePageExternalLink" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function updateVenuePageExternalLink({
  supabase,
  linkId,
  label,
  url,
}: {
  supabase: SupabaseClient<Database>;
  linkId: number;
  label: string;
  url: string;
}) {
  const { data, error } = await supabase
    .from("venue_pages_external_links")
    .update({
      label,
      url,
    })
    .eq("id", linkId)
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "updateVenuePageExternalLink" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function deleteVenuePageExternalLink({
  supabase,
  linkId,
}: {
  supabase: SupabaseClient<Database>;
  linkId: number;
}) {
  const { error } = await supabase
    .from("venue_pages_external_links")
    .delete()
    .eq("id", linkId);

  if (error) {
    logException(error, {
      extra: { data: "deleteVenuePageExternalLink" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return true;
}

export async function getVenuePageFAQs(
  supabase: SupabaseClient<Database>,
  venuePageId: string
) {
  const { data, error } = await supabase
    .from("venue_pages_faqs")
    .select("*")
    .eq("venue_page_id", venuePageId);

  if (error) {
    logException(error, {
      extra: { data: "getVenuePageFAQs" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function checkVenuePageHasFAQs(
  supabase: SupabaseClient<Database>,
  venuePageId: string
) {
  const data = await getVenuePageFAQs(supabase, venuePageId);
  return data.length > 0;
}

export async function createVenuePageFAQ({
  supabase,
  venuePageId,
  question,
  answer,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  question: string;
  answer: string;
}) {
  const { data, error } = await supabase
    .from("venue_pages_faqs")
    .insert({
      venue_page_id: venuePageId,
      question,
      answer,
    })
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "createVenuePageFAQ" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function updateVenuePageFAQ({
  supabase,
  faqId,
  question,
  answer,
}: {
  supabase: SupabaseClient<Database>;
  faqId: number;
  question: string;
  answer: string;
}) {
  const { data, error } = await supabase
    .from("venue_pages_faqs")
    .update({
      question,
      answer,
    })
    .eq("id", faqId)
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "updateVenuePageFAQ" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function deleteVenuePageFAQ({
  supabase,
  faqId,
}: {
  supabase: SupabaseClient<Database>;
  faqId: number;
}) {
  const { error } = await supabase
    .from("venue_pages_faqs")
    .delete()
    .eq("id", faqId);

  if (error) {
    logException(error, {
      extra: { data: "deleteVenuePageFAQ" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return true;
}

export async function getVenuePageTheme(
  supabase: SupabaseClient<Database>,
  venueSlug: VenuePagesRow["slug"]
) {
  const { data, error } = await supabase
    .from("venue_pages_themes")
    .select("*")
    .eq("venue_page_slug", venueSlug)
    .maybeSingle(); // Use maybeSingle to handle case where no theme exists

  if (error) {
    logException(error, {
      extra: { data: "getVenuePageTheme" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

/** Just the theme colours — no id, slug, or timestamps. */
export type VenuePageThemeColors = Pick<
  Tables<"venue_pages_themes">,
  | "accent_color"
  | "header_background_color"
  | "header_border_color"
  | "logo_background_color"
  | "top_section_background_color"
  | "top_section_gradient_color"
>;

type VenuePageThemeResponse = Promise<VenuePageThemeColors>;

export async function getVenuePageThemeWithFallback(
  supabase: SupabaseClient<Database>,
  venueSlug: VenuePagesRow["slug"]
): VenuePageThemeResponse {
  const { data, error } = await supabase
    .from("venue_pages_themes")
    .select("*")
    .eq("venue_page_slug", venueSlug)
    .maybeSingle(); // Use maybeSingle to handle case where no theme exists

  if (error) {
    logException(error, {
      extra: { data: "getVenuePageTheme" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  // if there's no theme for this venue, return a default one
  if (data === null) {
    // this is the "base" theme that we can fall back to
    return {
      accent_color: "#FF5E25",
      header_background_color: "#F5F0E8",
      header_border_color: "#e5e5e5",
      logo_background_color: "#000000",
      top_section_background_color: "#ffffff",
      top_section_gradient_color: null,
    };
  }

  const sanitizedData = {
    accent_color: data.accent_color,
    header_background_color: data.header_background_color,
    header_border_color: data.header_border_color,
    logo_background_color: data.logo_background_color,
    top_section_background_color: data.top_section_background_color,
    top_section_gradient_color: data.top_section_gradient_color,
  };

  return sanitizedData;
}

export async function getVenuePageThemesBySlug(
  supabase: SupabaseClient<Database>,
  venueSlugs: VenuePagesRow["slug"][]
): Promise<Map<string, Tables<"venue_pages_themes">>> {
  if (venueSlugs.length === 0) {
    return new Map();
  }

  const { data, error } = await supabase
    .from("venue_pages_themes")
    .select("*")
    .in("venue_page_slug", venueSlugs);

  if (error) {
    logException(error, {
      extra: { data: "getVenuePageThemesBySlug" },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  const themesMap = new Map<string, Tables<"venue_pages_themes">>();
  for (const theme of data ?? []) {
    themesMap.set(theme.venue_page_slug, theme);
  }
  return themesMap;
}
